import { useEditor, EditorContent } from '@tiptap/react';
import { useState, useEffect, useRef } from 'react'
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import { Bold, Italic, Underline, List, ListOrdered, Image as ImageIcon } from "lucide-react";
import { createLinkExtension, linkEditorProps } from './link-extension';
import { createImageExtension } from './image-resize-extension';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TemplateSelector } from './templates/TemplateSelector';
import { SenderSelect } from './SenderSelect';
import { LinkPopover } from './LinkPopover';
import { VariableSelector } from './templates/VariableSelector';
import { cn } from "@/lib/utils";
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { captureError } from '@/lib/sentry';
import { uploadEmailImage, ImageUploadError } from '@/lib/email/image-upload';

interface SequenceEmailComposerProps {
  defaultSubject?: string;
  defaultBody?: string;
  defaultSenderId?: string;
  saveLabel?: string;
  showDraftButton?: boolean;
  disabled?: boolean;
  draftDisabled?: boolean;
  showTestAccount?: boolean;
  hideActions?: boolean;
  // Hide the From / Subject rows. Used by the campaign wizard's per-recipient
  // inline editor, where the sender is campaign-level (not per-recipient) and
  // the surrounding UI may supply its own framing.
  hideSender?: boolean;
  hideSubject?: boolean;
  onSave: (subject: string, body: string, senderId: string) => void;
  onDraftSave?: (subject: string, body: string, senderId: string) => void;
  onSubjectChange?: (subject: string) => void;
  onBodyChange?: (body: string) => void;
  onSenderChange?: (senderId: string) => void;
}

export function SequenceEmailComposer({
  defaultSubject = "",
  defaultBody = "",
  defaultSenderId = "",
  saveLabel = "Save Changes",
  showDraftButton = false,
  disabled = false,
  draftDisabled = false,
  showTestAccount = false,
  hideActions = false,
  hideSender = false,
  hideSubject = false,
  onSave,
  onDraftSave,
  onSubjectChange,
  onBodyChange,
  onSenderChange,
}: SequenceEmailComposerProps) {
  const [subject, setSubject] = useState(defaultSubject);
  const [characterCount, setCharacterCount] = useState(defaultSubject?.length || 0);
  const [senderId, setSenderId] = useState(defaultSenderId);
  const [variables, setVariables] = useState<string[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  // Track the last body the editor produced to avoid feedback loops with onBodyChange
  const lastEditorBodyRef = useRef(defaultBody);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // Editor selection captured the moment the toolbar Image button was
  // clicked, restored at insert time so the image lands at the cursor and
  // not at position 0 (file picker steals focus + async upload can lose
  // the editor's stored selection).
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  // Upload an image to the email-images bucket and insert <img src> into the
  // editor. Campaigns persist body_template, and the queue processor copies
  // that body into each per-recipient job — using base64 here would multiply
  // storage by N recipients. See src/lib/email/image-upload.ts.
  const handleImageInsert = async (file: File) => {
    if (!editor || !currentWorkspace) return;
    setImageUploading(true);
    try {
      const { url } = await uploadEmailImage(file, currentWorkspace.id);
      const chain = editor.chain().focus();
      if (savedSelectionRef.current) {
        chain.setTextSelection(savedSelectionRef.current);
        savedSelectionRef.current = null;
      }
      chain.setImage({ src: url, alt: file.name }).run();
      toast({ description: 'Image inserted at your cursor' });
    } catch (err) {
      const message =
        err instanceof ImageUploadError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to upload image';
      console.error('Error uploading campaign image:', err);
      captureError(err as Error, { feature: 'campaign-composer-image-upload' });
      toast({
        title: 'Image upload failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setImageUploading(false);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      createLinkExtension(),
      TiptapUnderline,
      // Click-to-resize image (Google-Docs-style corner/edge handles).
      // Images upload to email-images Storage bucket — allowBase64 defaults
      // to false so pasted/dropped data URLs don't bloat the campaign body.
      // The renderHTML override emits a `width` HTML attribute + inline
      // `width: Npx` style so resized images render correctly in real email
      // clients. See image-resize-extension.ts.
      //
      // inline: false (block image) to match EmailTemplateEditor. With
      // inline: true the resize NodeView wraps the image in
      // `float: left; display: inline-block`, so any text after the image
      // wraps to its RIGHT inside the editor — diverging from the template
      // editor, the wizard preview, and the actual send (all of which render
      // the serialized HTML with no NodeView/float, putting text below).
      // Campaign templates are authored block-style, so block here keeps the
      // compose surface faithful to what recipients receive.
      createImageExtension({
        inline: false,
        htmlClass: 'max-w-full h-auto rounded',
      }),
    ],
    content: defaultBody,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] px-4 py-2',
      },
      ...linkEditorProps(),
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageInsert(file);
              return true;
            }
          }
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const imageFiles = Array.from(files).filter((f) =>
          f.type.startsWith('image/'),
        );
        if (imageFiles.length > 0) {
          event.preventDefault();
          imageFiles.forEach(handleImageInsert);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      lastEditorBodyRef.current = content;
      const regex = /{{([^}]+)}}/g;
      const matches = [...content.matchAll(regex)];
      const extractedVars = matches.map(match => match[1].trim());
      setVariables([...new Set(extractedVars)]);
      onBodyChange?.(content);
    }
  });
  
  useEffect(() => {
    // Update subject when defaultSubject changes
    setSubject(defaultSubject);
    setCharacterCount(defaultSubject?.length || 0);
  }, [defaultSubject]);

  useEffect(() => {
    // Only call setContent for external changes (e.g. template selection restoring state),
    // not when the parent echoes back what the editor itself produced via onBodyChange.
    if (editor && defaultBody !== lastEditorBodyRef.current) {
      editor.commands.setContent(defaultBody);
      lastEditorBodyRef.current = defaultBody;

      const regex = /{{([^}]+)}}/g;
      const matches = [...defaultBody.matchAll(regex)];
      const extractedVars = matches.map(match => match[1].trim());
      setVariables([...new Set(extractedVars)]);
    }
  }, [editor, defaultBody]);

  useEffect(() => {
    setSenderId(defaultSenderId);
  }, [defaultSenderId]);

  // Ensure test account is selected when showTestAccount is true
  useEffect(() => {
    if (showTestAccount && !senderId) {
      setSenderId('test-account');
    }
  }, [showTestAccount, senderId]);

  const handleImageButtonClick = () => {
    // Capture the editor selection right before the file picker opens so we
    // can restore it on insert. See savedSelectionRef declaration for why.
    if (editor) {
      const { from, to } = editor.state.selection;
      savedSelectionRef.current = { from, to };
    }
    imageInputRef.current?.click();
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach(handleImageInsert);
    }
    event.target.value = '';
  };

  const handleSave = () => {
    if (!editor) return;
    onSave(subject, editor.getHTML(), senderId);
  };

  const handleDraftSave = () => {
    if (!editor) return;
    if (onDraftSave) {
      onDraftSave(subject, editor.getHTML(), senderId);
    } else {
      onSave(subject, editor.getHTML(), senderId);
    }
  };

  return (
    <div className={cn(
      "w-full bg-card border rounded-lg shadow-sm flex flex-col",
      "transition-all duration-200 ease-in-out min-h-[450px]"
    )}>
      {(!hideSender || !hideSubject) && (
        <div className="p-4 space-y-4 border-b">
          {!hideSender && (
            <div className="flex items-center gap-2">
              <span className="w-16 text-sm font-medium">From:</span>
              <SenderSelect
                value={senderId || ''}
                onValueChange={(value) => {
                  setSenderId(value);
                  onSenderChange?.(value);
                }}
                showTestAccount={showTestAccount}
              />
            </div>
          )}
          {!hideSubject && (
            <div className="flex items-center gap-2">
              <span className="w-16 text-sm font-medium">Subject:</span>
              <div className="flex-1 relative">
                <Input
                  placeholder="Subject"
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    setCharacterCount(e.target.value.length);
                    onSubjectChange?.(e.target.value);
                  }}
                  maxLength={100}
                  className="pr-16"
                />
                <span className={cn(
                  "absolute right-3 top-1/2 transform -translate-y-1/2 text-xs",
                  characterCount > 80 ? "text-amber-600" : "text-muted-foreground"
                )}>
                  {characterCount}/100
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="px-4 py-2 border-b bg-muted/30 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 flex-wrap">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={cn("h-8 w-8 p-0", editor?.isActive('bold') && "bg-muted")}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={cn("h-8 w-8 p-0", editor?.isActive('italic') && "bg-muted")}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            className={cn("h-8 w-8 p-0", editor?.isActive('underline') && "bg-muted")}
          >
            <Underline className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-6 mx-1" />
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            className={cn("h-8 px-2", editor?.isActive('heading', { level: 2 }) && "bg-muted")}
          >
            <span className="font-bold text-sm">H1</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            className={cn("h-8 px-2", editor?.isActive('heading', { level: 3 }) && "bg-muted")}
          >
            <span className="font-bold text-sm">H2</span>
          </Button>
          
          <Separator orientation="vertical" className="h-6 mx-1" />
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={cn("h-8 w-8 p-0", editor?.isActive('bulletList') && "bg-muted")}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className={cn("h-8 w-8 p-0", editor?.isActive('orderedList') && "bg-muted")}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          
          <Separator orientation="vertical" className="h-6 mx-1" />
          
          <LinkPopover editor={editor} />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleImageButtonClick}
            disabled={imageUploading || !currentWorkspace}
            className="h-8 w-8 p-0"
            aria-label="Insert image"
            title={imageUploading ? 'Uploading…' : 'Insert image'}
          >
            <ImageIcon className="h-4 w-4" />
          </Button>
          <input
            type="file"
            ref={imageInputRef}
            className="hidden"
            accept="image/png,image/jpeg,image/gif,image/webp"
            multiple
            onChange={handleImageFileChange}
          />

          <VariableSelector
            onSelect={(variable) => {
              editor?.commands.insertContent(variable);
              editor?.commands.focus();
            }}
          />
        </div>
        
        <TemplateSelector
          density="wizard"
          onSelect={(template) => {
            setSubject(template.subject);
            setCharacterCount(template.subject.length);
            onSubjectChange?.(template.subject);

            // Replace editor content. TipTap's setContent defaults to
            // emitUpdate=false, which suppresses the onUpdate handler — and
            // onUpdate is the ONLY place we propagate body changes back to
            // the parent via onBodyChange. Without an explicit propagation
            // here, the wizard's composeBody stays at its previous value
            // (typically '') and the campaign is created with an empty
            // body_template — recipients receive an email containing only
            // the compliance footer. See incident 2026-06-01.
            editor?.commands.setContent(template.body);
            onBodyChange?.(template.body);
            // The defaultBody useEffect above uses lastEditorBodyRef to
            // avoid re-setContent loops. Update it here so the effect
            // doesn't immediately overwrite the editor with the same
            // content when the parent's composeBody state catches up.
            lastEditorBodyRef.current = template.body;

            // Extract variables from the new template
            const regex = /{{([^}]+)}}/g;
            const matches = [...template.body.matchAll(regex)];
            const extractedVars = matches.map(match => match[1].trim());
            setVariables([...new Set(extractedVars)]);
          }}
        />
      </div>

      {/* Internal scroll so pasting a long email body doesn't grow the
          wizard step beyond the viewport. max-h caps the editor; min-h keeps
          a usable typing area for short campaigns. */}
      <div className="flex-1 max-h-[50vh] min-h-[200px] overflow-y-auto border-b">
        <EditorContent editor={editor} className="h-full" />
      </div>

      {variables.length > 0 && (
        <div className="px-4 py-2 border-b bg-muted/20">
          <div className="text-xs font-medium text-muted-foreground mb-1">Variables in use</div>
          <div className="flex flex-wrap gap-1">
            {variables.map((variable) => (
              <Badge key={variable} variant="secondary" className="text-xs">
                {variable}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {!hideActions && (
        <div className="p-4 border-t bg-muted/30">
          <div className="flex justify-end gap-2">
            {showDraftButton && (
              <Button
                variant="outline"
                onClick={handleDraftSave}
                disabled={draftDisabled || !editor || !subject.trim()}
              >
                Save as Draft
              </Button>
            )}
            <Button
              variant="default"
              onClick={handleSave}
              disabled={disabled || !editor || !subject.trim()}
            >
              {saveLabel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}